<template>
<div>
	<div v-if="!showPrint" class="cart-detail">
		<div class="detail-heading">
			<label>Cart ID</label>
			<label-required :label="'Cart Type'" :condition="cartDetail.shopping_cart_type_id" />
			<label>Valid Until</label>
			<label>Date Created</label>
			<label>Total</label>
			<div><strong>{{ cartDetail.shopping_cart_id }}</strong></div>
			<select v-model="cartDetail.shopping_cart_type_id" @change="updateCart()">
				<option value="">Select...</option>
				<option v-for="(cartType, index) in shoppingCartTypes" :value="cartType.shopping_cart_type_id">{{ cartType.description }}</option>
			</select>
			<input type="date" v-model="cartDetail.date_expires" @change="updateCart()">
			<div>{{ cartDetail.date_created }}</div>
			<div>${{ cartDetail.totals.total }}</div>
		</div>

		<div class="detail-columns" :class="[ (cartDetail.shopping_cart_type_code == 'GUNSMITHING' || cartDetail.shopping_cart_type_code == 'WORK_ORDER') ? 'column-five' : '' ]">
			<div>
				<label>Customer</label>
				<div v-if="defaultCustomer">
					Default Customer <label>Anonymous Sale</label>
				</div>
				<div v-else>
					<div>{{ $fullName(cartDetail.billing) }}</div>
					<div v-if="cartDetail.billing.phone_number" style="margin: 0.5em 0 0 0;">
						<label>Phone</label><div>{{ cartDetail.billing.phone_number }}</div>
					</div>
					<div v-if="cartDetail.billing.email_address" style="margin: 0.5em 0.5em 0 0;">
						<label>Email</label><div>{{ cartDetail.billing.email_address }}</div>
					</div>
				</div>
			</div>
			<div>
				<label>Billing</label>
				<div v-if="defaultCustomer">
					Default Customer <label>Anonymous Sale</label>
				</div>
				<div v-else>
					<div>{{ cartDetail.billing.first_name + ' ' + cartDetail.billing.middle_name + ' ' + cartDetail.billing.last_name + ' ' + cartDetail.billing.suffix }}</div>
					<div v-if="cartDetail.billing.company_name">{{ cartDetail.billing.company_name }}</div>
					<div>{{ cartDetail.billing.address_1 }}</div>
					<div>{{ cartDetail.billing.city }}, {{ cartDetail.billing.state }} {{ cartDetail.billing.zip_code }}</div>
					<div style="margin: 1em 0 0 0;">
						<div class="button neutral inline" style="margin: 0 2em 0 0;" @click="editContact()">Edit</div>
						<a :href="$mapURLFromAddress(cartDetail.billing)" target='_blank'><i class="fas fa-map-marker-alt"></i> Map</a>
					</div>
				</div>
			</div>
			<div>
				<label>Shipping</label>
				<div v-if="cartDetail.shipping.full_name">
					<div>{{ cartDetail.shipping.full_name }}</div>
					<div v-if="cartDetail.shipping.company_name">{{ cartDetail.shipping.company_name }}</div>
					<div>{{ cartDetail.shipping.address_1 }}</div>
					<div>{{ cartDetail.shipping.city }}, {{ cartDetail.shipping.state }} {{ cartDetail.shipping.zip_code }}</div>
					<div style="margin: 1em 0 0 0;">
						<div class="button neutral inline" style="margin: 0 2em 0 0;">Edit</div>
						<a :href="mapURL(cartDetail.shipping)" target='_blank'><i class="fas fa-map-marker-alt"></i> Map</a>
					</div>
				</div>
				<div v-else>
					In store pickup
				</div>
			</div>
			<div v-if="cartDetail.shopping_cart_type_code == 'GUNSMITHING' || cartDetail.shopping_cart_type_code == 'WORK_ORDER'">
				<label>User Name</label>
				<div>{{ cartDetail.user_name || 'N/A' }}</div>
				<div style="margin: 0.5em 0px 0px 0px;">
					<label>Cart Status</label>
					<select v-model="cartDetail.shopping_cart_status_code_id">
						<option value="">Select...</option>
						<option v-for="status in shoppingCartStatusCodes" :value="status.shopping_cart_status_code_id">{{ status.description }}</option>
					</select>
					<div class="button action inline" style="margin: 0 2em 0 0;" @click="saveCartStatus()">Save Status</div>
					<div v-if="saveStatusMessage" class="message">{{ saveStatusMessage}}</div>
				</div>
			</div>
		</div>

		<div class="detail-section-heading"><div :class="['button', cartDetail.notes ? 'disabled' : 'action']" @click="showNotes = !showNotes">+</div>Notes</div>
		<div v-if="cartDetail.notes || showNotes" class="three-one-columns">
			<textarea v-model="cartDetail.notes" style="height: 10em; resize: auto;" />
			<div>
				<div class="button action inline" style="margin: 0 2em 0 0;" @click="saveCartNotes()">Save Notes</div>
				<div v-if="saveNoteMessage" class="message">{{ saveNoteMessage}}</div>
			</div>
		</div>

		<div v-if="cartDetail.shopping_cart_type_code == 'GUNSMITHING'">
			<div class="detail-section-heading"><div class="button action" @click="setNewFirearm()">+</div>Firearms</div>
			<div v-if="cartDetail.firearms.length > 0">
				<div class="cart-firearm-line cart-item-line-heading">
					<label>Manufacturer / Model</label>
					<label>Gun Type / Caliber</label>
					<label>Serial Number / Sequence</label>
					<label>Notes</label>
					<label></label>
					<label></label>
				</div>
				<div v-for="(firearm, index) in cartDetail.firearms" class="cart-firearm-line">
					<div>{{ firearm.manufacturer }}<br>{{ firearm.model }}</div>
					<div>{{ firearm.gun_type }}<br>{{ firearm.caliber }}</div>
					<div>{{ firearm.serial_number }}<br><span v-if="firearm.ffl_log_book_entry_id">{{ firearm.ffl_log_book_code }} - {{ firearm.sequence_number }}</span></div>
					<div>{{ firearm.notes }}</div>
					<div class="icon-control" @click="updatingFirearm = true, setNewFirearm(firearm)"><i class="fa fa-edit" /></div>
					<div class="icon-control" @click="confirmRemove('firearm', firearm)"><i class="fa fa-trash" /></div>
				</div>
			</div>
		</div>

		<div class="detail-section-heading"><div class="button action" @click="searchProducts = true">+</div>Items</div>
		<div v-if="localItems.length > 0">
			<div class="cart-item-line cart-item-line-heading">
				<label></label>
				<label>Item</label>
				<label>Source</label>
				<label class="right">Price</label>
				<label class="right">Qty</label>
				<label class="right">Ext</label>
			</div>
			<div v-for="(item, index) in localItems" class="cart-item-line" :key="'item_' + index + item.quantity">
				<div class="center"><img :src="item.image_src"></div>
				<div>
					<label style="padding: 0 0.5em 0 0;">{{ item.upc ? 'UPC' : 'ID' }}</label>{{ item.upc || item.product_id }}
					<span v-if="item.serial_number" style="padding: 0 0 0 1em;"><label>SN</label>{{ item.serial_number }}</span>
					<div>{{ item.description }}</div>
					<div v-if="item.notes"><label style="padding: 0 0.5em 0 0;">Note</label>{{ item.notes }}</div>
					<div v-if="item.discount_amount"><label style="padding: 0 0.5em 0 0;">Discount</label>${{ item.discount_amount }}, {{ item.override_reason }}</div>
				</div>
				<div>
					{{ item.distributor_code || 'Store' }} <label>({{ item.available }} Available)</label>
					<div>${{ item.cost || 'N/A' }}
						<span v-if="item.distributor_code && !item.in_cart" class="icon-control"><i class="fa fa-shopping-cart" @click="addToDealerCart(item)"/></span>
						<label v-if="item.in_cart" style="margin: 0 0 0 0.25em;">(In Cart)</label>
					</div>
				</div>
				<div class="right">${{ item.sale_price }}</div>
				<div class="right">{{ item.quantity }}</div>
				<div class="right">${{ (item.quantity * item.sale_price).toFixed(2) }}</div>
				<div class="icon-control" @click="assignProduct(item)"><i class="fa fa-edit" /></div>
				<div class="icon-control" @click="confirmRemove('item', item)"><i class="fa fa-trash" /></div>
			</div>
			<div class="cart-item-line">
				<div></div>
				<div></div>
				<div></div>
				<div></div>
				<div class="right"><label>Item Subtotal</label></div>
				<div class="right">${{ cartDetail.totals.subtotal }}</div>
			</div>
		</div>

		<div class="detail-section-heading"><div class="button action" @click="setNewFee()">+</div>Fees</div>
		<div v-if="cartDetail.fees.length > 0">
			<div v-for="(fee, index) in cartDetail.fees" class="cart-fee-line">
				<div>{{ fee.description }}</div>
				<div class="right">${{ fee.amount }}</div>
				<div class="icon-control" @click="setNewFee(fee)"><i class="fa fa-edit" /></div>
				<div class="icon-control" @click="confirmRemove('fee', fee)"><i class="fa fa-trash" /></div>
			</div>
		</div>

		<div class="detail-section-heading"><div :class="['button', cartDetail.totals.subtotal > 0 && cartDetail.discounts.length < 1 ? 'action' : 'disabled']" @click="setNewDiscount()">+</div>Discount</div>
		<div v-if="cartDetail.discounts.length > 0">
			<div v-for="(discount, index) in cartDetail.discounts" class="cart-discount-line">
				<div>{{ discount.description }}</div>
				<div>{{ discount.notes }}</div>
				<div class="right"><span v-if="discount.amount">$</span>{{ discount.amount }}</div>
				<div class="icon-control" @click="setNewDiscount(discount)"><i class="fa fa-edit" /></div>
				<div class="icon-control" @click="confirmRemove('discount', discount)"><i class="fa fa-trash" /></div>
			</div>
		</div>

		<div v-if="cartDetail.totals.total > 0">
			<div class="detail-section-heading">Totals</div>
			<div class="cart-item-line">
				<div></div>
				<div></div>
				<div></div>
				<div></div>
				<label class="right">Shipping</label>
				<div class="right">${{ cartDetail.totals.shipping_amount || '0.00' }}</div>
			</div>

			<div class="cart-item-line">
				<div></div>
				<div></div>
				<div></div>
				<div></div>
				<label class="right"><span class="icon-control" @click="setNewTaxExempt()"><i class="fa fa-edit"/></span> {{ cartDetail.tax_charge ? '' : 'Estimated' }} Tax{{  cartDetail.tax_reason.tax_reason ? ' Exempt' : '' }}</label>
				<div class="right">${{ cartDetail.tax_charge || cartDetail.totals.tax_amount || '0.00' }}</div>
			</div>

			<div class="cart-item-line">
				<div></div>
				<div></div>
				<div></div>
				<div></div>
				<label class="right">Total</label>
				<div class="right">${{ cartDetail.totals.total }}</div>
			</div>
		</div>

		<div v-if="cartDetail.payments.length > 0">
			<div class="detail-section-heading">Payments</div>
			<div class="cart-payment-line cart-item-line-heading">
				<label>Date</label>
				<label>Method</label>
				<label>Number</label>
				<label>Auth Code</label>
				<label class="right">Amount</label>
			</div>
			<div v-for="(payment, index) in cartDetail.payments" class="cart-payment-line">
				<div>{{ payment.payment_date }}</div>
				<div>{{ payment.description }}</div>
				<div>{{ payment.reference_number }}</div>
				<div>{{ payment.authorization_code }}</div>
				<div class="right">{{ payment.amount }}</div>
			</div>
		</div>

		<div class="modal" v-if="showFirearmModal">
			<div class="inner" style="max-width: 48em; max-height: 41em;">
				<h2>Add A Firearm to this Gunsmithing Order</h2>
				<div class="content" style="max-height: 31em; height: 26em; position: relative;">
					<div class="two-columns">
						<div>
							<label>Serial Number</label>
							<div class="input-with-control">
								<input type="search" ref="search_term" v-model="newFirearm.serial_number" placeholder="Search..." @search="searchSerialNumbers()" v-focus>
								<div class="button action input-go" @click="searchSerialNumbers()"><i class="fas fa-search"></i></div>
							</div>
						</div>
						<div>
							<label>Manufacturer</label>
							<input type="text" v-model="newFirearm.manufacturer">
						</div>
						<div>
							<label>Model</label>
							<input type="text" v-model="newFirearm.model">
						</div>
						<div>
							<label>Importer</label>
							<input type="text" v-model="newFirearm.importer">
						</div>
						<div>
							<label>Type</label>
							<select name="gun_type" v-model="newFirearm.gun_type">
								<option value="">Select...</option>
								<option v-for="gun_type in gunTypes" :value="gun_type">{{ gun_type }}</option>
							</select>
						</div>
						<div v-if="fflNumberInfo.manufacturer_importer">
							<label>Country of Manufacture</label>
							<input type="text" v-model="newFirearm.country_of_manufacture">
						</div>
						<div>
							<label>Caliber</label>
							<input type="text" v-model="newFirearm.caliber">
						</div>
						<div>
							<label>Log Book</label>
							<select name="logbook" v-model="newFirearm.ffl_log_book_id" :disabled="newFirearm.log_book_not_required">
								<option value="">Select...</option>
								<option v-for="logbook in logBooks" :value="logbook.ffl_log_book_id">{{ logbook.ffl_log_book_code }} - {{ logbook.description }}</option>
							</select>
						</div>
					</div>
					<div>
						<label>Notes</label>
						<input type="text" v-model="newFirearm.notes">
					</div>

					<div v-if="!newFirearm.ffl_log_book_entry_id" class="two-columns">
						<div>
							<span :class="['pointer', 'far', newFirearm.log_book_not_required ? 'fa-check-square' : 'fa-square']" @click="newFirearm.log_book_not_required = !newFirearm.log_book_not_required"></span>
							<label class="spaced">Log Book entry <b>NOT</b> required for this firearm.</label>
						</div>
						<div>
							<span :class="['pointer', 'far', newFirearm.not_used ? 'fa-check-square' : 'fa-square']" @click="newFirearm.not_used = !newFirearm.not_used"></span>
							<label class="spaced">Exclude this firearm from the Used Gun Report.</label>
						</div>
					</div>

					<div v-if="modalMessage" class="message center" v-html="modalMessage"></div>

					<div v-if="searchFirearmList.length" style="position: absolute; background-color: #fff; width: calc(100% - 2em); height: 100%; top: 0; left: 0; padding: 1em; margin: 4.5em 1em 1em 1em;">
						<div class="message" style="margin: 0 0 1em 0;">{{ searchFirearmList.length > 1 ? 'Multiple log book entries' : 'Existing log book entry ' }} found...</div>
						<ul class="clickable-list">
							<li v-for="(item, index) in searchFirearmList" class="clickable-list-item" @click="setFirearmFromSearch(item)">{{ item.sequence_code}} {{ item.manufacturer }} {{ item.model }}</li>
						</ul>
						<div class="message">
							<div v-if="searchFirearmList.length == 1" class="button inline-button neutral spaced" @click="setFirearmFromSearch(searchFirearmList[0])">Add This Firearm</div>
							<div v-else style="margin: 0 0 1em 0;">Select a log book entry from the list, or...</div>
							<div class="button inline-button neutral spaced" @click="newFirearm.duplicate_allowed = true; searchFirearmList = []">Receive Duplicate</div>
							<div class="button inline-button cancel spaced" @click="cancelReceiveDuplicate()">Cancel</div>
						</div>
					</div>

				</div>
				<div class="controls">
					<div class="button cancel spaced" @click="cancelAddFirearm()">Cancel</div>
					<div :class="['button', canAddFirearm ? 'action' : 'disabled']" @click="saveFirearm()">{{ newFirearm.ffl_log_book_entry_id ? 'Update' : 'Add' }} Firearm</div>
				</div>

			</div>
		</div>

		<div class="modal" v-if="showProductSearchModal">
			<div class="inner">
				<h2>{{ newShoppingCartItem.shopping_cart_item_id ? 'Update this Item' : 'Add an Item to this Cart' }}</h2>
				<div class="content">
						<div class="three-one-columns" style="align-items: center;">
							<div>
								<label>UPC</label>
								<div style="margin: 0 0 0.5em 0;">{{ newShoppingCartItem.upc || "&nbsp;" }}</div>
								<label>Description</label>
								<div style="margin: 0 0 0.5em 0;">{{ newShoppingCartItem.description || "&nbsp;" }}</div>
								<label v-if="newShoppingCartItem.manufacturer">Manufacturer</label>
								<div v-if="newShoppingCartItem.manufacturer" style="margin: 0 0 0.5em 0;">{{ newShoppingCartItem.manufacturer }}</div>
							</div>
							<div>
								<img :src="newShoppingCartItem.image_src" />
							</div>
						</div>
						<div class="three-columns">
							<label>Cost</label>
							<label>Price</label>
							<label>Qty</label>
							<number-input :decimals='2' :disabled="!newShoppingCartItem.product_id" v-model="newShoppingCartItem.cost" :key="'cart_cost_' + newShoppingCartItem.product_id" />
							<number-input :decimals='2' :disabled="!newShoppingCartItem.product_id" v-model="newShoppingCartItem.price" :key="'cart_price_' + newShoppingCartItem.product_id" />
							<input type="number" min="1" :max="newShoppingCartItem.available" v-model="newShoppingCartItem.quantity" :disabled="!newShoppingCartItem.product_id">
						</div>
						<label>Note</label>
						<input type="text" v-model="newShoppingCartItem.notes" :disabled="!newShoppingCartItem.product_id">
					</div>
				<div class="controls">
					<div class="button cancel spaced" @click="cancelAddProduct()">Cancel</div>
					<div :class="['button', newShoppingCartItem.product_id && newShoppingCartItem.price ? 'action' : 'disabled']" @click="saveProduct()">{{ newShoppingCartItem.shopping_cart_item_id ? 'Update' : 'Add' }} This Item</div>
				</div>
			</div>
		</div>

		<div class="modal" v-if="showFeeModal">
			<div class="inner">
				<h2>Add A Fee to this {{ cartDetail.shopping_cart_type }}</h2>
				<div class="content">
					<p v-if="cartDetail.totals.subtotal > 0">Enter an amount <b>or</b> a percent, and a description...</p>
					<p v-else>Enter an amount and description...</p>
					<div class="two-columns">
						<div>
							<label>Amount</label>
							<number-input :decimals='2' :disabled="newFee.percent > 0" v-model="newFee.amount" :key="'fee_amount_' + newFee.percent"/>
						</div>
						<div v-if="cartDetail.totals.subtotal > 0">
							<label>Percent</label>
							<number-input :decimals='2' :disabled="newFee.amount > 0" v-model="newFee.percent" :key="'fee_percent_' + newFee.amount" />
						</div>
					</div>
					<label-required :label="'Description'" :condition="newFee.description" />
					<input type="text" v-model="newFee.description">
					<div v-if="modalMessage" class="message center">{{ modalMessage }}</div>
				</div>
				<div class="controls">
					<div class="button cancel spaced" @click="showFeeModal = false">Cancel</div>
					<div class="button action" @click="saveFee()">{{ newFee.shopping_cart_fee_id ? 'Update' : 'Add' }} Fee</div>
				</div>
			</div>
		</div>

		<div class="modal" v-if="showDiscountModal">
			<div class="inner">
				<h2>Add A Discount to this {{ cartDetail.shopping_cart_type }}</h2>
				<div class="content">
					<p>Enter an amount, a percent, or a new item subtotal...</p>
					<div class="three-columns">
						<label>Amount</label>
						<label>Percent</label>
						<label>Subtotal</label>
						<number-input :decimals='2' :disabled="newDiscount.percentage > 0 || newDiscount.total > 0" v-model="newDiscount.amount" :key="'discount_amount_' + newDiscount.percentage + newDiscount.total" />
						<number-input :decimals='1' :disabled="newDiscount.amount > 0 || newDiscount.total > 0" v-model="newDiscount.percentage" :key="'discount_percentage_' + newDiscount.amount + newDiscount.total" />
						<number-input :decimals='2' :disabled="newDiscount.amount > 0 || newDiscount.percentage > 0" v-model="newDiscount.total" :key="'discount_total_' + newDiscount.percentage + newDiscount.amount" />
					</div>
					<label-required :label="'Reason'" :condition="newDiscount.reason_code_id" />
					<select v-model="newDiscount.reason_code_id">
						<option value="">Select...</option>
						<option v-for="(reasonCode, index) in reasonCodes" :value="reasonCode.reason_code_id">{{ reasonCode.description }}</option>
					</select>
					<label>Note</label>
					<input type="text" v-model="newDiscount.notes">
					<div v-if="modalMessage" class="message center">{{ modalMessage }}</div>
				</div>
				<div class="controls">
					<div class="button cancel spaced" @click="showDiscountModal = false">Cancel</div>
					<div class="button action" @click="saveDiscount()">Save Discount</div>
				</div>
			</div>
		</div>

		<div class="modal large-modal" v-if="showContactModal">
			<div class="inner">
				<h2>Edit Customer</h2>
				<div class="content">
					<contact-detail-form :clientId="cartDetail.client_id" :contact="editedBilling" :hideControls="true" @update="setEditedBilling" />
				</div>
				<div class="controls">
					<div class="button cancel spaced" @click="showContactModal = false">Cancel</div>
					<div :class="['button', customerInfoComlete ? 'action' : 'disabled']" @click="saveCustomer(editedBilling)">Save Customer</div>
				</div>
			</div>
		</div>
		<div class="modal" v-if="showRemoveModal">
			<div class="inner">
				<h2>{{ removeModalHeader }}</h2>
				<div class="content">{{ removeModalContent }}</div>
				<div class="modal-icon remove">
					<i class="fa fa-trash" aria-hidden="true"></i>
				</div>
				<div v-if="modalMessage" class="message center">{{ modalMessage }}</div>
				<div class="controls">
					<div class="button cancel spaced" @click="showRemoveModal = false">Cancel</div>
					<div class="button delete" @click="removeLineItem()">Remove {{ removeLineItemType }}</div>
				</div>
			</div>
		</div>
		<div class="modal" v-if="showTaxExemptModal">
			<div class="inner">
				<h2>Tax Exempt</h2>
				<div class="content">
					<p v-if="cartDetail.tax_reason.tax_reason">This {{ cartDetail.shopping_cart_type }} is currently set as tax exempt.</p>
					<p v-else>This {{ cartDetail.shopping_cart_type }} is not currently set as tax exempt.</p>
					<label-required :label="'Reason'" :condition="cartDetail.tax_reason.reason_code_id" />
					<select v-model="cartDetail.tax_reason.reason_code_id">
						<option value="">Select...</option>
						<option v-for="(reasonCode, index) in reasonCodes" :value="reasonCode.reason_code_id">{{ reasonCode.description }}</option>
					</select>
					<label>Note</label>
					<input type="text" v-model="cartDetail.tax_reason.notes">
					<div v-if="modalMessage" class="message center">{{ modalMessage }}</div>
				</div>
				<div class="controls">
					<div class="button cancel spaced" @click="showTaxExemptModal = false">Cancel</div>
					<div class="button neutral spaced" @click="saveTaxExempt('remove')">Remove</div>
					<div :class="['button', cartDetail.tax_reason.reason_code_id ? 'action' : 'disabled', 'spaced']" @click="saveTaxExempt()">Save Tax Exempt</div>
				</div>
			</div>
		</div>

	</div>

	<div v-if="showPrint" id="packing-slip">
		<div style="font-family: Helvetica, Arial, sans-serif; font-size: 14px;">
			<table style="width: 100%; font-size: 14px;">
				<tr>
					<td style="vertical-align: bottom;">
						<div><img :src="cartDetail.logo_src" style="max-width: 100px;" /></div>
					</td>
					<td style="vertical-align: bottom;">
						{{ getClient.company_name }}<br>
						{{ getClient.address_1 }}<br>
						{{ getClient.city }} {{ getClient.state }} {{ getClient.zip_code }}<br>
					</td>
					<td style="vertical-align: bottom;">
						<table style="width: 100%; font-size: 14px;">
							<tr v-if="getClient.phone_number">
								<td><label>Phone</label></td><td>{{ getClient.phone_number }}</td>
							</tr>
							<tr v-if="getClient.email_address">
								<td><label>Email</label></td><td>{{ getClient.email_address }}</td>
							</tr>
						</table>
					</td>
				</tr>
			</table>

			<h2>{{ cartDetail.shopping_cart_type_code }}</h2>

			<table :class="cartDetail.date_expires  ? 'four' : 'three'" style="width: 100%; font-size: 14px;">
				<tr>
					<td>
						<h3>Date Created</h3>
						{{ cartDetail.date_created }}
					</td>
					<td v-if="cartDetail.date_expires">
						<h3>Valid Through</h3>
						{{ cartDetail.date_expires }}
					</td>
					<td>
						<h3>Total</h3>
						{{ cartDetail.totals.total }}
					</td>
					<td>
						<h3>ID</h3>
						<strong>{{ cartDetail.shopping_cart_id }}</strong>
						<div class="barcode">
							<img :src="`data:image/jpeg;charset=utf-8;base64,${cartDetail.barcode}`">
						</div>
					</td>
				</tr>
			</table>

			<table class="three" style="width: 100%; font-size: 14px;">
				<tr>
					<td>
						<h3>Customer</h3>
						<div>{{ $fullName(cartDetail.billing) }}</div>
						<div v-if="cartDetail.billing.phone_number" style="margin: 0.5em 0 0 0;">
							<label>Phone</label><div>{{ cartDetail.billing.phone_number }}</div>
						</div>
						<div v-if="cartDetail.billing.email_address" style="margin: 0.5em 0.5em 0 0;">
							<label>Email</label><div>{{ cartDetail.billing.email_address }}</div>
						</div>
					</td>
					<td>
						<h3>Billing</h3>
						<div>{{ cartDetail.billing.first_name + ' ' + cartDetail.billing.middle_name + ' ' + cartDetail.billing.last_name + ' ' + cartDetail.billing.suffix }}</div>
						<div v-if="cartDetail.billing.company_name">{{ cartDetail.billing.company_name }}</div>
						<div>{{ cartDetail.billing.address_1 }}</div>
						<div>{{ cartDetail.billing.city }}, {{ cartDetail.billing.state }} {{ cartDetail.billing.zip_code }}</div>
					</td>
					<td>
						<h3>Shipping</h3>
						<div v-if="cartDetail.shipping.full_name">
							<div>{{ cartDetail.shipping.full_name }}</div>
							<div v-if="cartDetail.shipping.company_name">{{ cartDetail.shipping.company_name }}</div>
							<div>{{ cartDetail.shipping.address_1 }}</div>
							<div>{{ cartDetail.shipping.city }}, {{ cartDetail.shipping.state }} {{ cartDetail.shipping.zip_code }}</div>
						</div>
						<div v-else>
							In store pickup
						</div>
					</td>
				</tr>
			</table>

			<div v-if="cartDetail.notes">
				<h3>Notes</h3>
				{{ cartDetail.notes }}
			</div>

			<div style="border-bottom: 1px solid #999; margin: 0 0 1em 0; padding: 1em 0 0 0;"></div>

			<div v-if="cartDetail.shopping_cart_type_code == 'GUNSMITHING'">
				<h3>Firearms</h3>
				<table style="width: 100%; font-size: 14px;">
					<tr>
						<th style="text-align: left;">Manufacturer / Model</th>
						<th style="text-align: left;">Gun Type / Caliber</th>
						<th style="text-align: left;">Serial Number / Sequence</th>
						<th style="text-align: left;">Notes</th>
					</tr>
					<tr v-for="(firearm, index) in cartDetail.firearms">
						<td>{{ firearm.manufacturer + (firearm.importer ? '/' + firearm.importer : '') }}<br>{{ firearm.model }}</td>
						<td>{{ firearm.gun_type }}<br>{{ firearm.caliber }}</td>
						<td>{{ firearm.serial_number }}<br><span v-if="firearm.ffl_log_book_entry_id">{{ firearm.ffl_log_book_code }} - {{ firearm.sequence_number }}</span></td>
						<td>{{ firearm.notes }}</td>
					</tr>
				</table>
				<div style="border-bottom: 1px solid #999; margin: 0 0 1em 0; padding: 1em 0 0 0;"></div>
			</div>

			<div v-if="localItems.length > 0">
				<h3>Items</h3>
				<table style="width: 100%; font-size: 14px;">
					<tr>
						<th style="text-align: left;"></th>
						<th style="text-align: left;">Item</th>
						<th v-if="!cartDetail.exclude_cost" style="text-align: left;">Source</th>
						<th style="text-align: right;">Price</th>
						<th style="text-align: right;">Qty</th>
						<th style="text-align: right;" width="140">Ext</th>
					</tr>

					<tr v-for="(item, index) in localItems" class="cart-item-line">
						<td class="center"><img :src="item.image_src" style="max-width: 50px;"></td>
						<td>
							<label style="padding: 0 0.5em 0 0;">{{ item.upc ? 'UPC' : 'ID' }}</label>{{ item.upc || item.product_id }}
							<span v-if="item.serial_number" style="padding: 0 0 0 1em;"><label>SN</label>{{ item.serial_number }}</span>
							<div>{{ item.description }}</div>
							<div v-if="item.notes"><label style="padding: 0 0.5em 0 0;">Note</label>{{ item.notes }}</div>
							<div v-if="item.discount_amount"><label style="padding: 0 0.5em 0 0;">Discount</label>${{ item.discount_amount }}, {{ item.override_reason }}</div>
						</td>
						<td v-if="!cartDetail.exclude_cost">
							{{ item.distributor_code || 'Store' }} <label>({{ item.available }} Available)</label>
							<div v-if="item.cost && cartDetail.shopping_cart_type_code != 'QUOTE'">${{ item.cost }}</div>
						</td>
						<td style="text-align: right;" >${{ item.sale_price }}</td>
						<td style="text-align: right;" >{{ item.quantity }}</td>
						<td style="text-align: right;" >${{ (item.quantity * item.sale_price).toFixed(2) }}</td>
					</tr>
					<tr>
						<td></td>
						<td></td>
						<td v-if="!cartDetail.exclude_cost"></td>
						<td></td>
						<td style="text-align: right;" ><label>Item Subtotal</label></td>
						<td style="text-align: right;"  width="140">${{ cartDetail.totals.subtotal }}</td>
					</tr>
				</table>
				<div style="border-bottom: 1px solid #999; margin: 0 0 1em 0; padding: 1em 0 0 0;"></div>
			</div>

			<div v-if="cartDetail.fees.length > 0">
				<h3>Fees</h3>
				<table style="width: 100%; font-size: 14px;">
					<tr v-for="(fee, index) in cartDetail.fees" class="cart-fee-line">
						<td>{{ fee.description }}</td>
						<td style="text-align: right;"  width="140">${{ fee.amount }}</td>
					</tr>
				</table>
				<div class="divider"></div>
			</div>

			<div v-if="cartDetail.discounts.length > 0">
				<h3>Discount</h3>
				<table style="width: 100%; font-size: 14px;">
					<tr v-for="(discount, index) in cartDetail.discounts" class="cart-discount-line">
						<td>{{ discount.description }}</td>
						<td>{{ discount.notes }}</td>
						<td style="text-align: right;"  width="140">${{ discount.amount }}</td>
					</tr>
				</table>
				<div style="border-bottom: 1px solid #999; margin: 0 0 1em 0; padding: 1em 0 0 0;"></div>
			</div>

			<div v-if="cartDetail.totals.total > 0">
				<table style="width: 100%; font-size: 14px;">
					<tr>
						<td style="text-align: right;" >Shipping</td>
						<td style="text-align: right;" width="140">${{ cartDetail.totals.shipping_amount || '0.00' }}</td>
					</tr>
					<tr>
						<td style="text-align: right;" >Tax{{  cartDetail.tax_reason.tax_reason ? ' Exempt' : '' }}</td>
						<td style="text-align: right;"  width="140">${{ cartDetail.totals.tax_amount || '0.00'  }}</td>
					</tr>
					<tr>
						<td style="text-align: right;" >Total</td>
						<td style="text-align: right;" width="140">${{ cartDetail.totals.total }}</td>
					</tr>
				</table>
				<div style="border-bottom: 1px solid #999; margin: 0 0 1em 0; padding: 1em 0 0 0;"></div>
			</div>

			<div v-if="cartDetail.shopping_cart_type_code != 'QUOTE' && cartDetail.payments.length > 0">
				<h3>Payments</h3>
				<table style="width: 100%; font-size: 14px;">
					<tr v-for="(payment, index) in cartDetail.payments">
						<td>Date</td>
					</tr>
				</table>
				<div style="border-bottom: 1px solid #999; margin: 0 0 1em 0; padding: 1em 0 0 0;"></div>
			</div>

			<div v-if="cartDetail.shopping_cart_type_code == 'GUNSMITHING' || cartDetail.shopping_cart_type_code == 'WORK_ORDER'">
				<h3>Received From Customer</h3>
				<table style="width: 100%; font-size: 14px;">
					<tr>
						<td>
							<label>Date Received</label>
							<div style="margin: 1.5em 0 0 0;">_____/_____/_____</div>
						</td>
						<td>
							<label>Customer Signature</label>
							<div style="margin: 1.5em 0 0 0;">_________________________________</div>
						</td>
						<td>
							<div style="font-size: 0.8em; padding: 1em; width: 24em;">Customer Signature indicates<br>transfer of firearm(s) and item(s) listed above<br>for services.</div>
						</td>
					</tr>
				</table>

				<h3 style="margin: 1em 0 0 0;">Returned To Customer</h3>
				<table style="width: 100%; font-size: 14px;">
					<tr>
						<td>
							<label>Date Returned</label>
							<div style="margin: 1.5em 0 0 0;">_____/_____/_____</div>
						</td>
						<td>
							<label>Customer Signature</label>
							<div style="margin: 1.5em 0 0 0;">_________________________________</div>
						</td>
						<td>
							<div style="font-size: 0.8em; padding: 1em; width: 24em;">Customer Signature indicates<br>acceptance of work performed and receipt of<br>firearm(s) and item(s) listed above.</div>
						</td>
					</tr>
				</table>
			</div>
			<div v-if="cartDetail.shopping_cart_type_code == 'GUNSMITHING' && cartDetail.gunsmithing_terms != ''" class="gunsmithing-terms">
				<h3>Gunsmithing Terms and Conditions</h3>
				<div v-html="cartDetail.gunsmithing_terms" />
			</div>
		</div>
	</div>

	<product-search v-if="searchProducts" singleProduct="true" @assignProduct="assignProduct" @cancel="searchProducts = false" />

</div>

</template>

<script>
import LabelRequired from '@/components/LabelRequired'
import NumberInput from '@/components/NumberInput.vue'
import DateInput from '@/components/DateInput.vue'
import ContactDetailForm from '@/components/ContactDetailForm.vue'
import ProductSearch from '@/components/ProductSearch.vue'
export default {
	name: 'order-detail',
	props: [ 'cartDetail', 'shoppingCartTypes', 'shoppingCartStatusCodes', 'showPrint' ],
    components: {
        'label-required': LabelRequired,
		'number-input': NumberInput,
		'date-input': DateInput,
        'contact-detail-form': ContactDetailForm,
		'product-search': ProductSearch
	},
	data() {
		return {
			localItems: [],
			showNotes: false,
			saveNoteMessage: '',
			saveStatusMessage: '',
			showFirearmModal: false,
			searchFirearmList: [],
			newFirearm: {},
			duplicateSerialNumberAllowed: false,
			gunTypes: [],
			logBooks: [],
			fflNumberInfo: {},
			searchProducts: false,
			showProductSearchModal: false,
			newShoppingCartItem: {},
			showFeeModal: false,
			newFee: {},
			showDiscountModal: false,
			newDiscount: {},
			reasonCodes: [],
			editedBilling: {},
			showContactModal: false,
			showTaxExemptModal: false,
			modalMessage: '',
			search_term: '',
			searching: false,
			saving: false,
			didSearch: false,
			showRemoveModal: false,
			removeLineItemType: '',
			removeLineItemInfo: {},
			removeModalHeader: '',
			removeModalContent: '',
			updatingFirearm: false,
		}
	},
	watch: {
		showRemoveModal(newValue, oldValue) {
			if (oldValue === true && newValue === false) {
				this.resetRemoveModal();
			}
		},
		'cartDetail.items': {
			immediate: true,
			handler(newValue) {
				this.localItems = []
				for (const [key, item] of Object.entries(newValue)) {
				item.adding_to_cart = 0
				this.localItems.push(item)
		}
			}
		}

	},
	created() {
		// localItems enables click to update in_cart, adding_to_cart
		// for (const [key, item] of Object.entries(this.cartDetail.items)) {
		// 	item.adding_to_cart = 0
		// 	this.localItems.push(item)
		// }
		// moved to watch pattern to support page reactivity
	},
	computed: {
		defaultCustomer() {
			return this.cartDetail.contact_id == this.getClient.contact_id
		},
		customerInfoComlete() {
			return ((this.editedBilling.first_name && this.editedBilling.last_name) || this.editedBilling.company_name)
					&& this.editedBilling.address_1 && this.editedBilling.city && this.editedBilling.state && this.editedBilling.zip_code
		},
		canAddFirearm() {
			return this.searchFirearmList.length == 0 && this.newFirearm.manufacturer && this.newFirearm.model && this.newFirearm.gun_type && this.newFirearm.caliber && this.newFirearm.serial_number
		}
	},
	methods: {
		mapURL(data) {
			let mapLink = data['address_1'] + "+";
			if (data['address_2']) {
				mapLink += data['address_2'] + "+";
			}
			mapLink += data['city'] + "+";
			mapLink += data['state'] + "+";
			mapLink += data['zip_code'] + "+";
			mapLink += "&hl=en&t=m&z=11";
			return "https://maps.google.com/maps?q=" + encodeURI(mapLink)
		},
		editContact() {
			this.editedBilling = JSON.parse(JSON.stringify(this.cartDetail.billing))
			this.showContactModal = true
		},
		setEditedBilling(contact) {
			this.editedBilling = contact
		},
		saveCustomer() {
			if (!this.customerInfoComlete) { return }
			let contactChanged = false
			for (const [key, value] of Object.entries(this.editedBilling)) {
				if (this.cartDetail.billing[key] != value) {
					contactChanged = true
					break
				}
			}
			if (!contactChanged) {
				this.showContactModal = false
				return
			}
			this.apiCall({ destination: 'save_item_contact', data: this.editedBilling }).then(obj => {
				if (obj.status == 'success') {
					for (const [key, value] of Object.entries(this.editedBilling)) {
						if (this.cartDetail.billing[key] != value) {
							this.cartDetail.billing[key] = value
						}
					}
					this.showContactModal = false
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getLogbookControlLists() {
			this.apiCall({ destination: 'get_log_book_control_lists' }).then(obj => {
				if (obj.status == 'success') {
					this.logBooks = obj.logbooks
					this.gunTypes = obj.gun_types
					this.fflNumberInfo = obj.ffl_number_info
				}
			}).catch(err => {
				console.log(err)
			})
		},
 		updateCart() {
			this.apiCall({ destination: 'save_item_shopping_cart_detail', data: { use_shopping_cart_id: this.cartDetail.shopping_cart_id, cart_detail: this.cartDetail } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.$emit('update', this.cartDetail.shopping_cart_id);
				} else {
					this.saveMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
 		setNewFirearm(firearm) {
			if (this.gunTypes.length == 0 || this.logBooks.length == 0) {
				this.getLogbookControlLists()
			}
			if (firearm) {
				this.newFirearm = { ...firearm }
			} else {
				this.newFirearm = {
					gun_type: '',
					ffl_log_book_id: '',
					log_book_not_required: false,
					not_used: false
				}
			}
			this.searchFirearmList = []
			this.modalMessage = ''
			this.showFirearmModal = true
		},
		searchSerialNumbers() {
			if (!this.newFirearm.serial_number) { return }
			this.searching = true
			this.modalMessage = ''
			this.apiCall({ destination: 'get_list_ffl_log_book_entries', data: { search_term: this.newFirearm.serial_number, on_hand: true, exact_serial_number: true } }).then(obj => {
				this.searching = false
				this.didSearch = true
				if (obj.status == 'success') {
					if (obj.list_data.length == 0) {
						this.modalMessage = "Serial number <b>" + this.newFirearm.serial_number + "</b> is not currently in your log books."
					} else {
						this.searchFirearmList = obj.list_data
					}
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setFirearmFromSearch(item) {
			this.newFirearm = { ...item }
			this.searchFirearmList = []
		},
		cancelReceiveDuplicate() {
			this.searchFirearmList = []
			this.newFirearm.serial_number = ''
			this.newFirearm.duplicate_allowed = false
			this.didSearch = false
		},
		cancelAddFirearm() {
			this.showFirearmModal = false
			this.newFirearm = {}
			this.didSearch = false
		},
		saveFirearm() {
			this.modalMessage = ""
			if (this.saving) { return }
			if (this.searchFirearmList.length > 0) { return }
			if (!this.newFirearm.manufacturer || !this.newFirearm.model || !this.newFirearm.gun_type || !this.newFirearm.caliber || !this.newFirearm.serial_number) {
				this.modalMessage = "Please enter all firearm info"
				return
			}
			if (!this.newFirearm.log_book_not_required && !this.newFirearm.ffl_log_book_id) {
				this.modalMessage = "Please select a Log Book"
				return
			}
			if (!this.didSearch && !this.updatingFirearm) {
				this.searchSerialNumbers()
				return
			}
			this.saving = true
			this.apiCall({ destination: 'save_order_cart_firearm', data: { use_shopping_cart_id: this.cartDetail.shopping_cart_id, contact_id: this.cartDetail.contact_id, firearm: this.newFirearm } }).then(obj => {
				this.saving = false
				this.updatingFirearm = false
				if (obj.status == 'success') {
					this.$emit('update', this.cartDetail.shopping_cart_id);
					this.showFirearmModal = false
					this.newFirearm = {}
					this.didSearch = false
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		confirmRemove(typeId, typeInfo) {
			this.removeLineItemInfo = typeInfo
			this.removeLineItemType = typeId
			this.removeModalHeader = `Remove ${typeId}`
			this.removeModalContent =  `Do you want to permanently remove this ${typeId}?` 
			this.showRemoveModal = true;
		},
		resetRemoveModal() {
			this.removeLineItemType = ''
			this.removeLineItemInfo = {}
			this.removeModalHeader = ''
			this.removeModalContent = ''
			this.modalMessage = ''
			this.showRemoveModal = false;
		},
		removeLineItem() {
			switch (this.removeLineItemType) {
				case 'firearm':
					this.removeFirearm(this.removeLineItemInfo)
					break
				case 'item':
					this.removeProduct(this.removeLineItemInfo)
					break
				case 'fee':
					this.removeFee(this.removeLineItemInfo)
					break
				case 'discount':
					this.removeDiscount(this.removeLineItemInfo)
					break
				default:
					this.resetRemoveModal()
					break
			}	
		},
		removeFirearm(firearm) {
			if (this.saving) { return }
			this.saving = true
			this.apiCall({ destination: 'remove_order_cart_firearm', data: { use_shopping_cart_id: this.cartDetail.shopping_cart_id, firearm: firearm } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.showRemoveModal = false;
					this.$emit('update', this.cartDetail.shopping_cart_id);
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		assignProduct(item) {
			this.newShoppingCartItem = { ...item }
			if (!this.newShoppingCartItem.shopping_cart_item_id) {
				this.newShoppingCartItem.quantity = 1
			}
			// this.newShoppingCartItem.quantity = 1
			this.showProductSearchModal = true
			this.searchProducts = false
		},
		cancelAddProduct() {
			this.showProductSearchModal = false
			this.newShoppingCartItem = {}
			this.search_term = ''
		},
		saveProduct() {
			if (this.saving || !this.newShoppingCartItem.product_id || !this.newShoppingCartItem.price) { return }
			this.saving = true
			this.apiCall({ destination: 'add_to_order_cart', data: { use_shopping_cart_id: this.cartDetail.shopping_cart_id, item: this.newShoppingCartItem } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.cancelAddProduct()
					this.$emit('update', this.cartDetail.shopping_cart_id);
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		removeProduct(item) {
			if (this.saving) { return }
			this.saving = true
			this.apiCall({ destination: 'remove_from_order_cart', data: { use_shopping_cart_id: this.cartDetail.shopping_cart_id, product_id: item.product_id } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.showRemoveModal = false;
					this.$emit('update', this.cartDetail.shopping_cart_id);
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		addToDealerCart(item) {
			if (item.adding_to_cart) { return false }
			item.adding_to_cart = true
			let quantity = item.quantity || 1
			this.apiCall({ destination: 'add_to_dealer_cart', data: { product_id: item.product_id, quantity: quantity } }).then(obj => {
				if (obj.status == 'success') {
					item.adding_to_cart = false
					item.in_cart = quantity
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setNewFee(fee) {
			this.newFee = fee || {}
			this.showFeeModal = true
		},
		saveFee() {
			if (this.saving) { return }
			if ((!this.newFee.percent || !this.newFee.amount) && !this.newFee.description) { return }
			if (this.newFee.percent && this.cartDetail.totals.subtotal) {
				this.newFee.amount = (this.cartDetail.totals.subtotal * (this.newFee.percent / 100)).toFixed(2)
			}
			this.saving = true
			this.apiCall({ destination: 'save_order_cart_fee', data: { use_shopping_cart_id: this.cartDetail.shopping_cart_id, fee: this.newFee } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.$emit('update', this.cartDetail.shopping_cart_id);
					this.showFeeModal = false
					this.newFee = {}
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		removeFee(fee) {
			if (this.saving) { return }
			this.saving = true
			this.apiCall({ destination: 'remove_order_cart_fee', data: { use_shopping_cart_id: this.cartDetail.shopping_cart_id, fee: fee } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.showRemoveModal = false;
					this.$emit('update', this.cartDetail.shopping_cart_id);
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setNewDiscount(discount) {
			if (this.cartDetail.totals.subtotal == 0) { return }
			if (!discount && this.cartDetail.discounts.length) { return }
			if (this.reasonCodes.length == 0) {
				this.getReasonCodes()
			}
			if (discount) {
				this.newDiscount = discount
			} else {
				this.newDiscount = {
					reason_code_id: ''
				}
			}
			this.showDiscountModal = true
		},
		getReasonCodes() {
			if (this.savingNotes) { return }
			this.apiCall({ destination: 'get_list_reason_codes' }).then(obj => {
				this.reasonCodes = obj.reason_codes
			}).catch(err => {
				console.log(err)
			})
		},
		saveDiscount() {
			this.modalMessage = ''
			if (this.saving) { return }
			if ((!this.newDiscount.percentage && !this.newDiscount.amount && !this.newDiscount.total) || !this.newDiscount.reason_code_id) { return }
			this.saving = true
			this.apiCall({ destination: 'save_order_cart_discount', data: { use_shopping_cart_id: this.cartDetail.shopping_cart_id, discount: this.newDiscount } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.$emit('update', this.cartDetail.shopping_cart_id);
					this.showDiscountModal = false
					this.newDiscount = {}
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		removeDiscount(discount) {
			if (this.saving) { return }
			this.saving = true
			this.apiCall({ destination: 'remove_order_cart_discount', data: { use_shopping_cart_id: this.cartDetail.shopping_cart_id, discount: discount } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.showRemoveModal = false;
					this.$emit('update', this.cartDetail.shopping_cart_id);
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveCartStatus() {
			if (this.saving) { return }
			this.saving = true
			this.apiCall({ destination: 'save_item_cart_status', data: { use_shopping_cart_id: this.cartDetail.shopping_cart_id, shopping_cart_status_code_id: this.cartDetail.shopping_cart_status_code_id } }).then(obj => {
				this.saving = false
				this.saveStatusMessage = 'Status saved!'
				this.cartDetail.user_name = obj['shopping_cart']['user_name']
				setTimeout(() => { this.saveStatusMessage = '' }, 2000)
			}).catch(err => {
				console.log(err)
			})
		},
		saveCartNotes() {
			if (this.saving) { return }
			this.saving = true
			this.apiCall({ destination: 'save_item_cart_notes', data: { use_shopping_cart_id: this.cartDetail.shopping_cart_id, notes: this.cartDetail.notes } }).then(obj => {
				this.saving = false
				this.saveNoteMessage = 'Notes saved!'
				setTimeout(() => { this.saveNoteMessage = '' }, 2000)
			}).catch(err => {
				console.log(err)
			})
		},
		setNewTaxExempt() {
			if (this.cartDetail.totals.subtotal == 0) { return }
			if (this.reasonCodes.length == 0) {
				this.getReasonCodes()
			}
			this.showTaxExemptModal = true
		},
		saveTaxExempt(action) {
			if (this.saving) { return }
			if (!this.cartDetail.tax_reason.reason_code_id && action != 'remove') { return }
			if (action == 'remove') {
				this.cartDetail.tax_reason.reason_code_id = ''
				this.cartDetail.tax_reason.notes = ''
			}
			this.saving = true
			let postData = {
				use_shopping_cart_id: this.cartDetail.shopping_cart_id,
				reason_code_id: this.cartDetail.tax_reason.reason_code_id,
				notes: this.cartDetail.tax_reason.notes
			}
			this.apiCall({ destination: 'save_order_cart_tax_exempt', data: postData }).then(obj => {
				this.saving = false
				this.modalMessage = 'Tax exempt saved!'
				setTimeout(() => { 
					this.$emit('update', this.cartDetail.shopping_cart_id);
					this.modalMessage = '' 
					this.showTaxExemptModal = false
				}, 2000)
			}).catch(err => {
				console.log(err)
			})
		},
	}
}
</script>

<style lang="scss" scoped>
.gunsmithing-terms {
	padding-top: 2em;
	break-inside: avoid;
}
.barcode {
	img {
		width: auto !important;
		height: 20px !important;
	}
	span {
		display: block;
	}
}
.modal-icon.remove {
	text-align: center;
}
</style>